// AdminPages.tsx — /liff/admin/users + /liff/admin/invites
//
// Tenant-admin-only management surfaces. Users page renders the tenant's
// member directory with role chips; Invites page surfaces the binding-code
// table maintained by auth-api so an admin can issue / revoke codes from
// inside LINE.

import React, { useEffect, useState } from "react";
import { useLiff } from "../../../liff/index.js";
import { apiFetch } from "../../../lib/api-client.js";
import { useConfirmDialog } from "../../../dashboard/components/ConfirmDialog.js";
import {
  MobileShell, Card, CardHeader, MobileSpinner, EmptyMobile, ListRow,
  ComingSoonCard, PrimaryButton, SecondaryButton,
} from "../../../liff/components/Mobile.js";
import { formatTaipeiDateTime } from "../../../lib/time.js";

interface UserRow {
  id: string;
  display_name: string;
  role: string;
  bound_at_local?: string;
  email?: string;
  bound_via?: "email" | "line" | "both";   // PR auserlist-line-bound
  line_user_id?: string | null;
}
interface UsersResponse { items: UserRow[] }

/** Backend /v1/admin/users real shape — richer than UserRow above. */
interface BackendAdminUserRow {
  id: string;
  user_id: string | null;
  email: string | null;          // LINE-only members may have null email
  display_name: string | null;
  role: string;
  role_legacy: string | null;
  status: string;
  created_at: string;            // ISO
  last_login_at: number | null;
  super_admin: boolean;
  sso_providers: string[];
  bound_via?: "email" | "line" | "both";
  line_user_id?: string | null;
}

function reshapeAdminUsers(rows: BackendAdminUserRow[]): UserRow[] {
  return rows.map((r) => ({
    id: r.id,
    display_name: r.display_name ?? r.email ?? "—",
    role: r.role,
    email: r.email ?? undefined,
    bound_at_local: r.last_login_at
      ? formatTaipeiDateTime(r.last_login_at)
      : r.created_at
        ? formatTaipeiDateTime(r.created_at)
        : undefined,
    bound_via: r.bound_via ?? "email",
    line_user_id: r.line_user_id ?? null,
  }));
}

const ROLE_TONE: Record<string, string> = {
  tenant_admin:      "bg-accent/15 text-accent",
  manager:           "bg-info/15 text-info",
  officer:           "bg-success/15 text-success",
  incident_reporter: "bg-warning/15 text-warning",
  auditor:           "bg-text-muted/15 text-text-secondary",
};
const ROLE_LABEL: Record<string, string> = {
  tenant_admin: "管理員", manager: "主管", officer: "巡邏員",
  incident_reporter: "通報員", auditor: "稽核員", super_admin: "平台管理員",
};

/** /liff/admin/users — tenant member directory. */
export function AdminUsersPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<UsersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<UserRow | null>(null);

  useEffect(() => {
    if (!session) return;
    apiFetch(`/v1/admin/users?tenant_id=${encodeURIComponent(session.tenant_id)}`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) {
          const body = await r.json() as { items: BackendAdminUserRow[] };
          setData({ items: reshapeAdminUsers(body.items ?? []) });
        } else setError(`HTTP ${r.status}`);
      })
      .catch((e) => setError((e as Error).message));
  }, [session]);

  if (error) return <MobileShell title="使用者管理"><Card><p className="text-sm text-danger">無法載入：{error}</p></Card></MobileShell>;
  if (!data) return <MobileSpinner message="載入使用者列表" />;

  return (
    <MobileShell title="使用者管理" subtitle={`${data.items.length} 名成員`}>
      <Card>
        <CardHeader title="目前成員" />
        {data.items.length === 0 ? (
          <EmptyMobile title="尚無成員" />
        ) : (
          <ul className="space-y-2">
            {data.items.map((u) => {
              const canAssign = u.role === "officer" && !!u.line_user_id;
              return (
                <li key={u.id} className="rounded-xl bg-bg-raised p-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-sm font-medium truncate">{u.display_name}</p>
                    <div className="flex items-center gap-1 shrink-0">
                      {u.bound_via === "line" && (
                        <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-success/15 text-success">
                          LINE
                        </span>
                      )}
                      {u.bound_via === "both" && (
                        <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent/15 text-accent">
                          EMAIL+LINE
                        </span>
                      )}
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${ROLE_TONE[u.role] ?? "bg-bg-raised text-text-muted"}`}>
                        {ROLE_LABEL[u.role] ?? u.role}
                      </span>
                    </div>
                  </div>
                  <p className="text-[11px] text-text-muted truncate">
                    {u.email ?? "（LINE 綁定，未註冊 email）"} {u.bound_at_local && `· 加入 ${u.bound_at_local}`}
                  </p>
                  {canAssign && (
                    <button
                      onClick={() => setAssigning(u)}
                      className="mt-2 w-full h-9 rounded-lg bg-accent/10 text-accent text-xs font-medium hover:bg-accent/15"
                    >
                      指派案場
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <PrimaryButton href="/liff/admin/invites">產生邀請碼</PrimaryButton>

      {assigning && session && (
        <AssignSitesSheet
          row={assigning}
          tenantId={session.tenant_id}
          sessionToken={session.session_token}
          onClose={() => setAssigning(null)}
        />
      )}
    </MobileShell>
  );
}

/**
 * AssignSitesSheet — full-screen overlay for assigning an officer to sites.
 *
 * Flow:
 *   1. GET /v1/admin/officers — find officer record by line_user_id match.
 *   2. GET /v1/sites — all active sites in this tenant.
 *   3. GET /v1/admin/officers/:id/sites — current assignments.
 *   4. User toggles checkboxes → PUT /v1/admin/officers/:id/sites { site_ids }.
 *   5. Close sheet.
 */
interface BackendOfficer { id: string; line_user_id: string | null; display_name: string; }
interface BackendSite { id: string; name: string; address: string | null; risk_level: string; status: string; }
interface BackendAssignment { site_id: string; }
function AssignSitesSheet({ row, tenantId, sessionToken, onClose }: {
  row: UserRow; tenantId: string; sessionToken: string; onClose: () => void;
}): React.ReactElement {
  const [phase, setPhase] = useState<"loading" | "ready" | "saving" | "error">("loading");
  const [err, setErr] = useState<string | null>(null);
  const [officerId, setOfficerId] = useState<string | null>(null);
  const [sites, setSites] = useState<BackendSite[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [officersRes, sitesRes] = await Promise.all([
          apiFetch(`/v1/admin/officers?tenant_id=${encodeURIComponent(tenantId)}&status=active`, {}, { jwt: sessionToken }),
          apiFetch(`/v1/sites?tenant_id=${encodeURIComponent(tenantId)}&status=active&limit=500`, {}, { jwt: sessionToken }),
        ]);
        if (cancelled) return;
        if (!officersRes.ok) throw new Error(`officers ${officersRes.status}`);
        if (!sitesRes.ok) throw new Error(`sites ${sitesRes.status}`);
        const officersBody = await officersRes.json() as { items: BackendOfficer[] };
        const sitesBody = await sitesRes.json() as { items: BackendSite[] };
        const matched = officersBody.items.find(
          (o) => o.line_user_id && row.line_user_id && o.line_user_id === row.line_user_id,
        ) ?? officersBody.items.find((o) => o.display_name === row.display_name);
        if (!matched) {
          setErr("找不到此使用者的 officer 紀錄。");
          setPhase("error");
          return;
        }
        setOfficerId(matched.id);
        setSites(sitesBody.items.filter((s) => s.status === "active"));
        const assignsRes = await apiFetch(
          `/v1/admin/officers/${encodeURIComponent(matched.id)}/sites?tenant_id=${encodeURIComponent(tenantId)}`,
          {}, { jwt: sessionToken },
        );
        if (cancelled) return;
        if (assignsRes.ok) {
          const a = await assignsRes.json() as { items: BackendAssignment[] };
          setSelected(new Set(a.items.map((x) => x.site_id)));
        }
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        setErr((e as Error).message);
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, [row.line_user_id, row.display_name, tenantId, sessionToken]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    if (!officerId) return;
    setPhase("saving"); setErr(null);
    try {
      const r = await apiFetch(
        `/v1/admin/officers/${encodeURIComponent(officerId)}/sites?tenant_id=${encodeURIComponent(tenantId)}`,
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ site_ids: [...selected] }),
        },
        { jwt: sessionToken },
      );
      if (!r.ok) {
        const text = await r.text().catch(() => `HTTP ${r.status}`);
        throw new Error(text.slice(0, 240));
      }
      onClose();
    } catch (e) {
      setErr((e as Error).message);
      setPhase("ready");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-bg-base flex flex-col">
      <header className="px-4 py-3 border-b border-border-subtle flex items-center justify-between">
        <button onClick={onClose} className="text-sm text-text-muted">取消</button>
        <h2 className="text-sm font-semibold truncate mx-2">指派案場 — {row.display_name}</h2>
        <button
          onClick={() => void save()}
          disabled={phase !== "ready" || !officerId}
          className="text-sm font-semibold text-accent disabled:opacity-40"
        >
          {phase === "saving" ? "儲存中…" : "儲存"}
        </button>
      </header>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {phase === "loading" && <p className="text-sm text-text-muted">載入中…</p>}
        {phase === "error" && (
          <div className="rounded-xl bg-danger/10 text-danger text-sm p-3">{err ?? "載入失敗"}</div>
        )}
        {(phase === "ready" || phase === "saving") && (
          <>
            <p className="text-xs text-text-muted">
              共 {sites.length} 個案場，已選 {selected.size} 個。未勾選者將被撤銷指派。
            </p>
            {sites.length === 0 ? (
              <EmptyMobile title="此單位尚未建立任何案場" body="請先到「案場」頁建立案場。" />
            ) : (
              <ul className="space-y-2">
                {sites.map((s) => {
                  const checked = selected.has(s.id);
                  return (
                    <li key={s.id}>
                      <button
                        onClick={() => toggle(s.id)}
                        className={`w-full text-left rounded-xl p-3 flex items-center gap-3 ${checked ? "bg-accent/15 border border-accent/40" : "bg-bg-raised border border-transparent"}`}
                      >
                        <span className={`shrink-0 w-5 h-5 rounded border ${checked ? "bg-accent border-accent" : "border-text-muted"} flex items-center justify-center`}>
                          {checked && <span className="text-text-inverse text-xs">✓</span>}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{s.name}</p>
                          {s.address && <p className="text-[11px] text-text-muted truncate">{s.address}</p>}
                        </div>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                          s.risk_level === "high" ? "bg-danger/15 text-danger" :
                          s.risk_level === "medium" ? "bg-warning/15 text-warning" :
                          "bg-bg-raised text-text-muted"
                        }`}>
                          {s.risk_level === "high" ? "高" : s.risk_level === "medium" ? "中" : "低"}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {err && <div className="rounded-xl bg-danger/10 text-danger text-sm p-3">{err}</div>}
          </>
        )}
      </div>
    </div>
  );
}

interface InviteRow {
  code: string;
  role: string;
  created_at: number;          // unix seconds (binding_codes table type)
  expires_at: number;
  redeemed_at?: number | null;
  redeemed_by_line_user_id?: string | null;
  revoked_at?: number | null;
  status: "active" | "redeemed" | "revoked" | "expired";
}
interface InvitesResponse { items: InviteRow[] }

type InviteFilter = "active" | "redeemed" | "expired";
const FILTER_TABS: { key: InviteFilter; label: string }[] = [
  { key: "active",   label: "有效" },
  { key: "redeemed", label: "已兌換" },
  { key: "expired",  label: "已過期" },
];

/** /liff/admin/invites — list + issue binding codes. */
export function AdminInvitesPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<InvitesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [issuedCode, setIssuedCode] = useState<{ code: string; role: string } | null>(null);
  const [issueError, setIssueError] = useState<string | null>(null);
  const [filter, setFilter] = useState<InviteFilter>("active");
  const [revoking, setRevoking] = useState<string | null>(null);
  const dlg = useConfirmDialog();

  function reload(): void {
    if (!session) return;
    setError(null);
    apiFetch(`/v1/invites?status=${filter}&tenant_id=${encodeURIComponent(session.tenant_id)}`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) {
          const body = await r.json() as { results?: InviteRow[] };
          setData({ items: body.results ?? [] });
        } else setError(`HTTP ${r.status}`);
      })
      .catch((e) => setError((e as Error).message));
  }
  useEffect(reload, [session, filter]);

  async function revoke(code: string): Promise<void> {
    if (!session) return;
    if (!(await dlg.confirm(`撤銷邀請碼 ${code}？\n\n此碼將立即失效，已寄出給其他人也無法兌換。`, { destructive: true, okLabel: "撤銷" }))) return;
    setRevoking(code);
    try {
      const r = await apiFetch(
        `/v1/invites/${encodeURIComponent(code)}`,
        { method: "DELETE" }, { jwt: session.session_token },
      );
      if (r.ok || r.status === 404) reload();
      else {
        const text = await r.text().catch(() => `HTTP ${r.status}`);
        await dlg.alert(`撤銷失敗：${text.slice(0, 200)}`, { title: "撤銷失敗" });
      }
    } finally { setRevoking(null); }
  }

  async function issueCode(role: string): Promise<void> {
    if (!session) return;
    setIssuing(true);
    setIssueError(null);
    try {
      const r = await apiFetch(
        "/v1/invites",
        {
          method: "POST",
          headers: { "content-type": "application/json", "x-idempotency-key": `${session.user_id ?? "u"}-${Date.now()}` },
          body: JSON.stringify({ tenant_id: session.tenant_id, role }),
        },
        { jwt: session.session_token },
      );
      if (r.ok) {
        const body = await r.json() as { code: string };
        setIssuedCode({ code: body.code, role });
        reload();
      } else {
        const text = await r.text().catch(() => `HTTP ${r.status}`);
        setIssueError(text.slice(0, 240));
      }
    } catch (e) {
      setIssueError((e as Error).message ?? "network_error");
    } finally {
      setIssuing(false);
    }
  }

  if (error && !data) return <MobileShell title="邀請碼"><Card><p className="text-sm text-danger">無法載入：{error}</p></Card></MobileShell>;
  if (!data) return <MobileSpinner message="載入邀請碼列表" />;

  return (
    <MobileShell title="邀請碼" subtitle="新增成員 / 撤銷邀請">
      {issuedCode && (
        <Card>
          <CardHeader title="已建立邀請碼" hint={ROLE_LABEL[issuedCode.role] ?? issuedCode.role} />
          <p className="text-3xl font-mono font-semibold tracking-widest text-accent text-center my-3">
            {issuedCode.code}
          </p>
          <p className="text-xs text-text-secondary text-center mb-3">
            轉發此 8 碼給對方，他們在 LINE「綁定身分」頁輸入即可加入。
          </p>
          <PrimaryButton onClick={() => setIssuedCode(null)}>知道了</PrimaryButton>
        </Card>
      )}

      <Card>
        <CardHeader title="產生新邀請碼" />
        <div className="grid grid-cols-2 gap-2">
          {(["manager", "officer", "incident_reporter", "auditor"] as const).map((r) => (
            <button
              key={r}
              onClick={() => issueCode(r)}
              disabled={issuing}
              className="h-12 rounded-xl bg-bg-raised text-sm font-medium text-text-primary hover:bg-bg-hover disabled:opacity-40"
            >
              {issuing ? "建立中…" : `+ ${ROLE_LABEL[r]}`}
            </button>
          ))}
        </div>
        {issueError && <p className="text-xs text-danger bg-danger/10 rounded-lg p-3 mt-3">{issueError}</p>}
      </Card>

      <Card>
        <CardHeader title="邀請碼列表" hint={`${data.items.length} 筆`} />
        <div className="flex gap-1 mb-3 rounded-lg bg-bg-raised p-1">
          {FILTER_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              className={`flex-1 h-9 text-xs font-medium rounded-md transition-colors ${
                filter === t.key ? "bg-accent text-text-inverse" : "text-text-secondary"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {data.items.length === 0 ? (
          <EmptyMobile
            title={
              filter === "active" ? "目前沒有有效邀請碼"
              : filter === "redeemed" ? "尚無已兌換邀請碼"
              : "沒有過期的邀請碼"
            }
          />
        ) : (
          <ul className="space-y-2">
            {data.items.map((i) => {
              const tone = i.status === "active" ? "bg-success/15 text-success"
                : i.status === "redeemed" ? "bg-bg-base text-text-muted"
                : i.status === "expired" ? "bg-warning/15 text-warning"
                : "bg-danger/15 text-danger";
              const expiresLocal = i.expires_at ? formatTaipeiDateTime(i.expires_at * 1000) : "—";
              const redeemedLocal = i.redeemed_at ? formatTaipeiDateTime(i.redeemed_at * 1000) : null;
              return (
                <li key={i.code} className="rounded-xl bg-bg-raised p-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm font-mono font-semibold tracking-widest">{i.code}</span>
                    <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${tone}`}>{i.status}</span>
                  </div>
                  <p className="text-[11px] text-text-muted">
                    {ROLE_LABEL[i.role] ?? i.role} · {redeemedLocal ? `已兌換 ${redeemedLocal}` : `過期 ${expiresLocal}`}
                  </p>
                  {i.status === "active" && (
                    <button
                      onClick={() => void revoke(i.code)}
                      disabled={revoking === i.code}
                      className="mt-2 w-full h-8 rounded-md bg-danger/10 text-danger text-xs font-medium hover:bg-danger/20 disabled:opacity-40"
                    >
                      {revoking === i.code ? "撤銷中…" : "撤銷邀請"}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <SecondaryButton href="/liff/admin/users">回到使用者管理</SecondaryButton>
    </MobileShell>
  );
}
